import { Injectable } from '@angular/core';
import Konva from 'konva';
import { ProductGroup } from '../../planogram/models/product-group';
import { Block } from '../../planogram/models/block';
import { Point } from '../models/point';
import { Element } from '../../equipment/models/elements/element';
import { Equipment } from '../../equipment/models/equipment';
import { EquipmentElements } from '../../equipment/models/elements/equipmentElements';
import { Extension, ExtensionType, RoundingParams } from '../../equipment/models/elements/extension';
import { konvaSettings } from '../../equipment/models/konvaSettings';
import { Utils } from './utils.service';
import { UnitConverter } from '../utils/unitConverter';
import { SessionService } from './session.service';
import { canvasBlockParams, CanvasDrawerService, canvasElementParams, canvasProductParams } from './canvas-drawer.service';

const MAX_SCALE_VALUE: number = 14;

@Injectable()
export class CanvasDrawerDetailedService extends CanvasDrawerService {

	private _productDetailedGroups: WeakMap<ProductGroup, Konva.Group> = new WeakMap<ProductGroup, Konva.Group>();
	private _productGroupsDetailedTooltips: WeakMap<ProductGroup, Konva.Label> = new WeakMap<ProductGroup, Konva.Label>();
	private _detailedBlocks: WeakMap<Block, Konva.Group> = new WeakMap<Block, Konva.Group>();
	private _detailedBlocksDescriptionsTooltips: WeakMap<Block, Konva.Label> = new WeakMap<Block, Konva.Label>();
	private _detailedBlocksTooltips: WeakMap<Block, Konva.Label> = new WeakMap<Block, Konva.Label>();
	private _detailedEquipmentElements: WeakMap<Element, Konva.Group> = new WeakMap<Element, Konva.Group>();
	private readonly _stripesImage: HTMLImageElement;

	constructor(sessionService: SessionService) {
		super(sessionService);
		this._stripesImage = new Image();
		this._stripesImage.src = '/static/img/icons/stripes.svg';
	}

	drawDetailedProduct(params: canvasProductParams, canvasProduct: Konva.Group): void {
		canvasProduct.find('.face, .packed-image, .image, .image-frame, .analytics, .product-side, .face-eclipse').forEach((node: Konva.Node) => node.destroy());

		const pg: ProductGroup = params.productGroup;

		for (let i = 0; i < pg.FacesQty; i++) {
			const faceWidth: number = Utils.round(canvasProduct.width() / pg.FacesQty);
			const xPoint: number = Utils.round(i * faceWidth);

			for (let j = 1; j <= pg.placementDepth; j++) {
				const faceHeight: number = Utils.round(canvasProduct.height() / pg.placementDepth);
				const yPoint: number = Utils.round(j * faceHeight - faceHeight);

				this.drawProductFace(params, canvasProduct, xPoint, yPoint, faceWidth, faceHeight);
			}
		}
	}

	drawDetailedHookProduct(params: canvasProductParams, canvasProduct: Konva.Group): void {
		canvasProduct.find('.face, .packed-image, .image, .image-frame, .analytics, .product-side, .face-eclipse').forEach((node: Konva.Node) => node.destroy());

		const pg: ProductGroup = params.productGroup;
		const faceHeight: number = canvasProduct.height();
		const yPoint: number = 0;

		for (let j = 0; j < pg.getPlacementDepth; j++) {
			const faceWidth: number = Utils.round(canvasProduct.width() / pg.placementDepth);
			const xPoint: number = Utils.round(j * faceWidth);

			this.drawProductFace(params, canvasProduct, xPoint, yPoint, faceWidth, faceHeight);
		}
	}

	createCanvasDetailedProduct(params: canvasProductParams): Konva.Group {
		const pg: ProductGroup = params.productGroup,
			canvasProduct: Konva.Group = new Konva.Group({ name: 'productGroup inactive' });

		if (pg.element.parent.is(EquipmentElements.Hook, EquipmentElements.Hole)) {
			canvasProduct.setAttrs({
				id: String(pg.element.id),
				x: Utils.round((pg.begin.z - pg.Depth - params.normalize.x) * params.scale + konvaSettings.detailedCanvasOffset + params.beginPoint.x),
				y: Utils.round((params.stand.Height - pg.begin.y - pg.Height - params.normalize.y) * params.scale + konvaSettings.detailedCanvasOffset + params.beginPoint.y),
				width: Utils.round(pg.Depth * params.scale),
				height: Utils.round(pg.Height * params.scale)
			});
			this.drawDetailedHookProduct(params, canvasProduct);
		} else if (pg.isLooseForm) {
			canvasProduct.setAttrs({
				id: String(pg.element.id),
				x: Utils.round((pg.element.layoutBoundingX.min - params.normalize.x) * params.scale + konvaSettings.detailedCanvasOffset + params.beginPoint.x),
				y: Utils.round((pg.element.layoutBoundingZ.min - params.normalize.y) * params.scale + konvaSettings.detailedCanvasOffset + params.beginPoint.y),
				width: Utils.round(pg.Width * params.scale),
				height: Utils.round(pg.Depth * params.scale)
			});
			this.drawWeightProduct(params, canvasProduct);
		} else {
			const pgCoordinates: Array<Point> = pg.zCoordinates;

			canvasProduct.setAttrs({
				id: String(pg.element.id),
				x: Utils.round((pgCoordinates[3].x - params.normalize.x) * params.scale + konvaSettings.detailedCanvasOffset + params.beginPoint.x),
				y: Utils.round((pgCoordinates[3].y - params.normalize.y) * params.scale + konvaSettings.detailedCanvasOffset + params.beginPoint.y),
				width: Utils.round(pg.Width * params.scale),
				height: Utils.round(pg.Depth * params.scale),
				rotation: pg.angle
			});
			this.drawDetailedProduct(params, canvasProduct);
		}

		if (!pg.image.img.complete || pg.error || params.articleMode || ProductGroup.imageNotFound(pg.image.img.src)) {
			this.drawArticle(params, canvasProduct, true);
		}

		if (params.sequenceNumberMode) this.drawProductNumber(params, canvasProduct);

		if (!pg.image.img.complete && !params.articleMode) {
			this.loadImage(pg.image.img).then(() => {
				canvasProduct.find('.face, .analytics, .article').every(el => el.destroy());

				if (pg.element.parent.is(EquipmentElements.Hook, EquipmentElements.Hole)) {
					this.drawDetailedHookProduct(params, canvasProduct);
				} else if (pg.isLooseForm) {
					this.drawWeightProduct(params, canvasProduct);
				} else {
					this.drawDetailedProduct(params, canvasProduct);
				}

				if (!pg.image.img.complete || pg.error || params.articleMode || ProductGroup.imageNotFound(pg.image.img.src)) {
					this.drawArticle(params, canvasProduct, true);
				}

				if (params.sequenceNumberMode) this.drawProductNumber(params, canvasProduct);

				params.callback();
			});
		}

		if (params.showStockQty || params.showPrices || params.showAbcTurnover || params.showAbcReceipts) {
			const productGroup = pg.element.products.find((group: Array<ProductGroup>) => group.some((p: ProductGroup) => p == pg)),
				maxY = Utils.max([...productGroup.map((p: ProductGroup) => p.getBoundingY().min)]);

			if (pg.element.parent.is(EquipmentElements.Hook) || pg.getBoundingY().min == maxY) {
				this.drawMarkers(params, canvasProduct, false);
			}
		}

		if (this._productDetailedGroups.has(pg)) this._productDetailedGroups.get(pg)?.destroy();
		this._productDetailedGroups.set(pg, canvasProduct);
		return canvasProduct;
	}

	getCanvasDetailedProduct(pg: ProductGroup): Konva.Group {
		if (!this._productDetailedGroups.has(pg)) return this._defaultGroup;
		return this._productDetailedGroups.get(pg);
	}

	createCanvasDetailedProductTooltip(pg: ProductGroup, text: string, canvasElement: HTMLElement): Konva.Label {
		const tooltip = this.createCanvasTooltip(this.getCanvasDetailedProduct(pg), text, canvasElement);
		tooltip.id(String(pg.element.id));
		if (this._productGroupsDetailedTooltips.has(pg)) this._productGroupsDetailedTooltips.get(pg)?.destroy();
		this._productGroupsDetailedTooltips.set(pg, tooltip);
		return tooltip;
	}

	getCanvasDetailedProductTooltip(pg: ProductGroup): Konva.Label {
		if (!this._productGroupsDetailedTooltips.has(pg)) return new Konva.Label();
		return this._productGroupsDetailedTooltips.get(pg);
	}

	changeDetailedBlockBordersStroke(block: Block, activeBlock: Block): void {
		const nodes: Array<Konva.Node> = this.getCanvasDetailedBlockBorders(block).getChildren();

		if (block == activeBlock) {
			nodes.forEach((node: Konva.Node) => node.setAttr('stroke', konvaSettings.activeColor));
		} else {
			nodes.forEach((node: Konva.Node) => node.setAttr('stroke', block.color.slice(0, 7)));
		}
	}

	createCanvasDetailedBlock(params: canvasBlockParams): Konva.Group {
		if (this._detailedBlocks.has(params.block)) this._detailedBlocks.get(params.block)?.destroy();

		const block: Block = params.block,
			blockMinX: number = Math.max(block.begin[0].x, params.detailedElement.getBoundingX().min),
			blockMaxX: number = Math.min(block.boundingX.max, params.detailedElement.getBoundingX().max),
			blockMinZ: number = Math.max(block.begin[0].z, params.detailedElement.getBoundingZ().min),
			blockMaxZ: number = Math.min(block.boundingZ.max, params.detailedElement.getBoundingZ().max),
			blockGroup: Konva.Group = new Konva.Group({
				name: 'block',
				x: Utils.round((blockMinX - params.normalize.x) * params.scale + params.beginPoint.x + konvaSettings.detailedCanvasOffset),
				y: Utils.round((blockMinZ - params.normalize.y) * params.scale + params.beginPoint.y + konvaSettings.detailedCanvasOffset),
				width: Utils.round((blockMaxX - blockMinX) * params.scale),
				height: Utils.round((blockMaxZ - blockMinZ) * params.scale)
			}),
			blockBody: Konva.Rect = new Konva.Rect({
				name: 'block-body',
				draggable: params.draggable,
				width: blockGroup.width(),
				height: blockGroup.height()
			});

		if (block.image) {
			const blockBodyImage: HTMLImageElement = new Image();
			blockBodyImage.src = block.image;
			blockBodyImage.onload = () => {
				blockBody.fillPatternImage(blockBodyImage);
				blockBody.fillPatternRepeat('no-repeat');
				blockBody.fillPatternScale({
					x: blockGroup.width() / blockBodyImage.width,
					y: blockGroup.height() / blockBodyImage.height
				});
			}
		} else {
			blockBody.fill(!block.hasIntersectionsWithProducts()
				? block.color
				: `#ff0000${block.color.slice(7, 9) || 'ff'}`
			);
		}

		blockGroup.add(blockBody);
		this.drawBlockTitle(params, blockGroup);
		this.drawBlockBorders(params, blockGroup);
		blockGroup.findOne('.percent')?.destroy();

		this._detailedBlocks.set(block, blockGroup);

		if (params.selected) {
			this.getCanvasDetailedBlockBody(block).hide();
			this.getCanvasDetailedBlockTitle(block).hide();
		}

		return blockGroup;
	}

	getCanvasDetailedBlock(block: Block): Konva.Group {
		if (!this._detailedBlocks.has(block)) return this._defaultGroup;
		return this._detailedBlocks.get(block);
	}

	getCanvasDetailedBlockBody(block: Block): Konva.Rect {
		return this.getCanvasDetailedBlock(block).findOne('.block-body') || new Konva.Rect();
	}

	getCanvasDetailedBlockTitle(block: Block): Konva.Group {
		return this.getCanvasDetailedBlock(block).findOne('.title') || new Konva.Group();
	}

	getCanvasDetailedBlockName(block: Block): Konva.Text {
		return this.getCanvasDetailedBlock(block).findOne('.name') || new Konva.Text();
	}

	getCanvasDetailedBlockDescriptionIcon(block: Block): Konva.Image {
		return this.getCanvasDetailedBlock(block).findOne('.description-icon') || new Konva.Image({ image: new Image() });
	}

	getCanvasDetailedBlockTooltipNecessity(block: Block): boolean {
		const text: Konva.Text = this.getCanvasDetailedBlockName(block),
			originalName: string = text.text().replace(/ /g,''),
			truncatedName: string = text.textArr.map(v => v.text).join('').replace(/ /g,'');

		return originalName != truncatedName && !this.sessionService.personalSettings.blockNameWrap;
	}

	getCanvasDetailedBlockBorders(block: Block): Konva.Group {
		return this.getCanvasDetailedBlock(block).findOne('.borders') || new Konva.Group();
	}

	getCanvasDetailedBlockSizes(block: Block): Konva.Group {
		return this.getCanvasDetailedBlock(block).findOne('.sizes') || new Konva.Group();
	}

	createCanvasDetailedBlockDescriptionTooltip(block: Block, text: string, canvasElement: HTMLElement): Konva.Label {
		let wrappedText: string = '';
		let currentLine: string = '';

		text.split(' ').forEach((word: string) => {
			if ((currentLine + word).length > 50) {
				wrappedText += currentLine + '\n';
				currentLine = '';
			}
			currentLine += word + ' ';
		});
		wrappedText += currentLine;

		const tooltip = this.createCanvasTooltip(
			this.getCanvasDetailedBlockDescriptionIcon(block),
			wrappedText,
			canvasElement
		);
		if (this._detailedBlocksDescriptionsTooltips.has(block)) this._detailedBlocksDescriptionsTooltips.get(block)?.destroy();
		this._detailedBlocksDescriptionsTooltips.set(block, tooltip);
		return tooltip;
	}

	createCanvasDetailedBlockTooltip(block: Block, text: string, canvasElement: HTMLElement): Konva.Label {
		let wrappedText: string = '';
		let currentLine: string = '';

		text.split(' ').forEach((word: string) => {
			if ((currentLine + word).length > 50) {
				wrappedText += currentLine + '\n';
				currentLine = '';
			}
			currentLine += word + ' ';
		});
		wrappedText += currentLine;

		const tooltip = this.createCanvasTooltip(this.getCanvasDetailedBlockName(block), wrappedText, canvasElement);
		if (this._detailedBlocksTooltips.has(block)) this._detailedBlocksTooltips.get(block)?.destroy();
		this._detailedBlocksTooltips.set(block, tooltip);
		return tooltip;
	}

	getCanvasDetailedBlockTooltip(block: Block): Konva.Label {
		if (!this._detailedBlocksTooltips.has(block)) return new Konva.Label();
		return this._detailedBlocksTooltips.get(block);
	}

	createCanvasDetailedShelf(params: canvasElementParams, equipment: Equipment): Konva.Group {
		if (this._detailedEquipmentElements.has(params.element)) this._detailedEquipmentElements.get(params.element)?.destroy();

		const elementGroup: Konva.Group = new Konva.Group({
			name: params.element.parent.type,
			x: params.beginPoint.x + konvaSettings.detailedCanvasOffset + (params.element.shapes[0].begin.x - params.normalize.x) * params.scale,
			y: params.beginPoint.y + konvaSettings.detailedCanvasOffset + (params.element.shapes[0].begin.z - params.normalize.y) * params.scale,
			width: params.element.Width * params.scale,
			height: params.element.shapes[0].depth * params.scale
		});

		const shape = params.element.shapes[0], points: Array<number> = [];
		if (shape.type == 'trapeze') {
			if (shape.frontWidth >= shape.backWidth) {
				points.push(
					Utils.round((shape.frontWidth - shape.backWidth) / 2) * params.scale, 0,
					Utils.round((shape.frontWidth + shape.backWidth) / 2) * params.scale, 0,
					shape.frontWidth * params.scale, shape.depth * params.scale,
					0, shape.depth * params.scale
				);
			} else {
				points.push(
					0, 0,
					shape.backWidth * params.scale, 0,
					Utils.round((shape.backWidth + shape.frontWidth) / 2) * params.scale, shape.depth * params.scale,
					Utils.round((shape.backWidth - shape.frontWidth) / 2) * params.scale, shape.depth * params.scale
				);
			}

			elementGroup.add(new Konva.Line({
				name: 'stroke-dark stroke-active stroke-hover-light',
				points: points,
				closed: true,
				fill: konvaSettings.fillWhiteColor,
				stroke: params.active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
				strokeWidth: 1
			}));
		} else {
			const shelf: Konva.Rect = new Konva.Rect({
				name: 'stroke-dark stroke-active stroke-hover-light',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				fill: konvaSettings.fillWhiteColor,
				stroke: params.active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
				strokeWidth: 1
			});

			elementGroup.add(shelf);

			if (params.element.isExtensionActive(ExtensionType.Rounding)) {
				const rounding: Extension<RoundingParams> = params.element.getExtension<RoundingParams>(ExtensionType.Rounding);
				shelf.cornerRadius([
					Utils.convertPercentToRadius(rounding.params.leftSideRadius, params.element) * params.scale,
					Utils.convertPercentToRadius(rounding.params.rightSideRadius, params.element) * params.scale,
					Utils.convertPercentToRadius(rounding.params.rightSideRadius, params.element) * params.scale,
					Utils.convertPercentToRadius(rounding.params.leftSideRadius, params.element) * params.scale
				]);
			}
		}

		this._detailedEquipmentElements.set(params.element, elementGroup);

		return elementGroup;
	}

	createCanvasDetailedBackWall(params: canvasElementParams, equipment: Equipment): Konva.Group {
		if (this._detailedEquipmentElements.has(params.element)) this._detailedEquipmentElements.get(params.element)?.destroy();

		const elementGroup: Konva.Group = new Konva.Group({
			name: params.element.parent.type,
			x: params.beginPoint.x + konvaSettings.detailedCanvasOffset + (params.element.shapes[0].begin.x - params.normalize.x) * params.scale,
			y: params.beginPoint.y + konvaSettings.detailedCanvasOffset + (params.element.shapes[0].begin.z - params.normalize.y) * params.scale,
			width: params.element.shapes[0].width * params.scale,
			height: params.element.shapes[0].depth * params.scale
		});

		elementGroup.add(new Konva.Rect({
			name: 'dark active',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			fill: params.active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			stroke: params.active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1
		}));

		this._detailedEquipmentElements.set(params.element, elementGroup);

		return elementGroup;
	}

	createCanvasDetailedShowcase(params: canvasElementParams, equipment: Equipment): Konva.Group {
		if (this._detailedEquipmentElements.has(params.element)) this._detailedEquipmentElements.get(params.element)?.destroy();

		const elementGroup: Konva.Group = new Konva.Group({
			name: params.element.parent.type,
			x: params.beginPoint.x + konvaSettings.detailedCanvasOffset + (params.element.shapes[0].begin.x - params.normalize.x) * params.scale,
			y: params.beginPoint.y + konvaSettings.detailedCanvasOffset + (params.element.shapes[0].begin.z - params.normalize.y) * params.scale,
			width: params.element.shapes[0].width * params.scale,
			height: params.element.shapes[0].depth * params.scale
		});

		elementGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: params.active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1
		}));

		this._detailedEquipmentElements.set(params.element, elementGroup);

		return elementGroup;
	}

	createCanvasDetailedSideWalls(params: canvasElementParams, equipment: Equipment): Konva.Group {
		if (this._detailedEquipmentElements.has(params.element)) this._detailedEquipmentElements.get(params.element)?.destroy();

		const bounding = params.element.depend.getBoundingX(),
			elementGroup: Konva.Group = new Konva.Group({
				name: params.element.parent.type,
				x: params.beginPoint.x + konvaSettings.detailedCanvasOffset + (bounding.min - params.normalize.x) * params.scale,
				y: params.beginPoint.y + konvaSettings.detailedCanvasOffset + (params.element.shapes[0].begin.z - params.normalize.y) * params.scale,
				width: params.element.depend.Width * params.scale,
				height: params.element.depend.Depth * params.scale
			});

		elementGroup.add(new Konva.Rect({
			name: 'dark active',
			x: (params.element.shapes[0].begin.x - bounding.min) * params.scale,
			y: params.element.shapes[0].begin.z * params.scale,
			width: params.element.shapes[0].width * params.scale,
			height: params.element.shapes[0].depth * params.scale,
			rotation: (params.element.shapes[0] as any).angle,
			fill: params.active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			stroke: params.active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1
		}));

		elementGroup.add(new Konva.Rect({
			name: 'dark active',
			x: (params.element.shapes[1].begin.x - bounding.min) * params.scale,
			y: params.element.shapes[1].begin.z * params.scale,
			width: params.element.shapes[1].width * params.scale,
			height: params.element.shapes[1].depth * params.scale,
			rotation: (params.element.shapes[1] as any).angle,
			fill: params.active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			stroke: params.active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1
		}));

		this._detailedEquipmentElements.set(params.element, elementGroup);

		return elementGroup;
	}

	createCanvasDetailedBox(params: canvasElementParams, equipment: Equipment): Konva.Group {
		if (this._detailedEquipmentElements.has(params.element)) this._detailedEquipmentElements.get(params.element)?.destroy();

		const elementGroup: Konva.Group = new Konva.Group({
			name: params.element.parent.type,
			x: params.beginPoint.x + konvaSettings.detailedCanvasOffset + (params.element.shapes[0].begin.x - params.normalize.x) * params.scale,
			y: params.beginPoint.y + konvaSettings.detailedCanvasOffset + (params.element.shapes[0].begin.z - params.normalize.y) * params.scale,
			width: params.element.shapes[0].width * params.scale,
			height: params.element.shapes[0].depth * params.scale
		});
		let labelText: string, labelFill: string;

		if (!params.element.isExtensionActive(ExtensionType.Availability)) {
			labelText = 'N';
			labelFill = konvaSettings.fillGreyColor;
		} else {
			labelText = String(Utils.getElementIndex(params.element, equipment));
			labelFill = konvaSettings.fillWhiteColor;
		}

		const labelGroup: Konva.Group = this.createCanvasElementLabel(labelText, labelFill);

		elementGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active stroke-hover-light',
			x: params.element.shapes[0].wallWidth * params.scale / 2,
			y: params.element.shapes[0].wallWidth * params.scale / 2,
			width: elementGroup.width() - params.element.shapes[0].wallWidth * params.scale,
			height: elementGroup.height() - params.element.shapes[0].wallWidth * params.scale,
			fill: konvaSettings.fillWhiteColor,
			stroke: params.active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: params.element.shapes[0].wallWidth * params.scale
		}));

		labelGroup.setAttrs({
			x: elementGroup.width() - labelGroup.width() - params.element.shapes[0].wallWidth * params.scale,
			y: elementGroup.height() - labelGroup.height() - params.element.shapes[0].wallWidth * params.scale
		});

		elementGroup.add(labelGroup);

		this._detailedEquipmentElements.set(params.element, elementGroup);

		return elementGroup;
	}

	createCanvasDetailedPartition(params: canvasElementParams, equipment: Equipment): Konva.Group {
		if (this._detailedEquipmentElements.has(params.element)) this._detailedEquipmentElements.get(params.element)?.destroy();

		const elementGroup: Konva.Group = new Konva.Group({
			name: params.element.parent.type,
			x: params.beginPoint.x + konvaSettings.detailedCanvasOffset + (params.element.shapes[0].begin.x - params.normalize.x) * params.scale,
			y: params.beginPoint.y + konvaSettings.detailedCanvasOffset + (params.element.shapes[0].begin.z - params.normalize.y) * params.scale,
			width: params.element.shapes[0].width * params.scale,
			height: params.element.shapes[0].depth * params.scale
		});

		elementGroup.add(new Konva.Rect({
			name: 'dark active fill-hover-light',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			fill: params.active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			stroke: params.active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1
		}));

		this._detailedEquipmentElements.set(params.element, elementGroup);

		return elementGroup;
	}

	createCanvasDetailedHook(params: canvasElementParams, equipment: Equipment): Konva.Group {
		if (this._detailedEquipmentElements.has(params.element)) this._detailedEquipmentElements.get(params.element)?.destroy();

		const elementGroup: Konva.Group = new Konva.Group({
			name: params.element.parent.type,
			x: params.beginPoint.x + konvaSettings.detailedCanvasOffset + (params.element.shapes[0].begin.z - params.normalize.x) * params.scale,
			y: params.beginPoint.y + konvaSettings.detailedCanvasOffset + (equipment.Height - params.element.getBoundingY().max - params.normalize.y) * params.scale,
			width: params.element.shapes[0].depth * params.scale,
			height: params.element.shapes[0].height * params.scale
		});

		if (!params.element.isExtensionActive(ExtensionType.Availability)) {
			elementGroup.add(new Konva.Rect({
				name: 'disabled stroke-dark stroke-active',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				fill: konvaSettings.fillGreyColor,
				stroke: params.active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
				strokeWidth: 1
			}));
			void this.drawHiddenLines(elementGroup);
		} else {
			elementGroup.add(new Konva.Rect({
				name: 'dark active',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				fill: params.active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
				stroke: params.active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
				strokeWidth: 1
			}));
		}

		elementGroup.add(new Konva.Rect({
			x: -5,
			y: -0.01 * params.scale,
			width: 5,
			height: 0.2 * params.scale,
			fill: konvaSettings.fillDarkColor,
			stroke: konvaSettings.fillDarkColor,
			strokeWidth: 1
		}));

		this._detailedEquipmentElements.set(params.element, elementGroup);

		return elementGroup;
	}

	createCanvasDetailedEquipmentElement(params: canvasElementParams, equipment: Equipment): Konva.Group {
		params.element.shapes.forEach((shape) => (shape as any).representation = 'top');
		switch (params.element.parent.type) {
			case EquipmentElements.BaseRectangle:
			case EquipmentElements.BaseTrapeze:
			case EquipmentElements.Shelf:
			case EquipmentElements.AngleShelf: return this.createCanvasDetailedShelf(params, equipment);
			case EquipmentElements.BackWall:
			case EquipmentElements.CentralPillar:
			case EquipmentElements.HookWall:
			case EquipmentElements.PerforatedWall: return this.createCanvasDetailedBackWall(params, equipment);
			case EquipmentElements.Showcase: return this.createCanvasDetailedShowcase(params, equipment);
			case EquipmentElements.SideWalls: return this.createCanvasDetailedSideWalls(params, equipment);
			case EquipmentElements.Hook: return this.createCanvasDetailedHook(params, equipment);
			case EquipmentElements.Box:
			case EquipmentElements.SlicedBox: return this.createCanvasDetailedBox(params, equipment);
			case EquipmentElements.Partition: return this.createCanvasDetailedPartition(params, equipment);
			default: throw new Error(`No such element drawer ${params.element.parent.type}`);
		}
	}

	getCanvasDetailedEquipmentElement(element: Element): Konva.Group {
		if (!this._detailedEquipmentElements.has(element)) return this._defaultGroup;
		return this._detailedEquipmentElements.get(element);
	}

	setEquipmentElementActive(element: Element): void {
		const canvasElement: Konva.Group = this.getCanvasDetailedEquipmentElement(element);

		canvasElement.find('.active, .stroke-active')
			.forEach((shape: Konva.Shape) => shape.stroke(konvaSettings.activeColor));
		canvasElement.find('.active, .fill-active')
			.forEach((shape: Konva.Shape) => shape.fill(konvaSettings.activeColor));
	}

	setEquipmentElementInactive(element: Element): void {
		const canvasElement: Konva.Group = this.getCanvasDetailedEquipmentElement(element);

		canvasElement.find('.light, .stroke-light')
			.forEach((shape: Konva.Shape) => shape.stroke(konvaSettings.fillGreyColor));
		canvasElement.find('.dark, .stroke-dark')
			.forEach((shape: Konva.Shape) => shape.stroke(konvaSettings.fillDarkColor));
		canvasElement.find('.light, .fill-light')
			.forEach((shape: Konva.Shape) => shape.fill(konvaSettings.fillGreyColor));
		canvasElement.find('.dark, .fill-dark')
			.forEach((shape: Konva.Shape) => shape.fill(konvaSettings.fillDarkColor));
		canvasElement.find('.disabled')
			.forEach((shape: Konva.Shape) => shape.fill(konvaSettings.fillGreyColor));
	}

	setEquipmentElementHovered(element: Element): void {
		const canvasElement: Konva.Group = this.getCanvasDetailedEquipmentElement(element);

		canvasElement.find('.fill-hover-dark')
			.forEach((shape: Konva.Shape) => shape.fill('grey'));
		canvasElement.find('.fill-hover-light')
			.forEach((shape: Konva.Shape) => shape.fill('grey'));
		canvasElement.find('.stroke-hover-dark')
			.forEach((shape: Konva.Shape) => shape.stroke(konvaSettings.fillDarkColor));
		canvasElement.find('.stroke-hover-light')
			.forEach((shape: Konva.Shape) => shape.stroke('grey'));
	}

	createPlanogramDetailedSizes(params: canvasElementParams, equipment: Equipment): Array<Konva.Group> {
		const sizeGroups: Array<Konva.Group> = [];
		const elements = equipment.availableElements
			.filter((el: Element) => el == params.element || el.depend == params.element);

		let normalize: Point;

		if (params.normalize) {
			normalize = params.normalize;
		} else {
			let minX = params.element.getBoundingX().min,
				minY = 0;

			if (params.element.parent.is(EquipmentElements.Hook)) {
				minX = params.element.getBoundingZ().min;
				minY = equipment.Height - params.element.getBoundingY().min;
			}

			normalize = { x: minX, y: minY };
		}

		elements.forEach((element: Element) => {
			if (
				[
					'Hook',
					'Hole',
					'Box',
					'SlicedBox',
					'HookPanel',
					'Perforation',
					'SidesWall',
					'BackWall',
					'HookWall',
					'PerforationWall',
					'Showcase',
					'Partition'
				].includes(element.parent.type)
			)
				return;

			element.shapes.forEach((shape) => {
				const lines: Array<Array<number>> = shape.getLines(params.element.parent.type == 'Hook' ? 'side' : 'top', equipment);
				const bLabelGroup: Konva.Group = new Konva.Group({});
				const lLabelGroup: Konva.Group = new Konva.Group({});
				const bLine: Array<number> = lines[2];

				let text: string = String(UnitConverter.getConvertValue(
					Utils.distance(bLine[0], bLine[1], bLine[2], bLine[3]), 'distance', 'inches', 3
				));
				let center: Point = Utils.center(bLine[0], bLine[1], bLine[2], bLine[3]);

				const bText: Konva.Text = new Konva.Text({
					text: text,
					fontSize: Math.min((params.zoom - 100) / 10 + 10, MAX_SCALE_VALUE),
					offsetY: -4,
					offsetX: -3
				});
				const bRect: Konva.Rect = new Konva.Rect({
					width: bText.getWidth() + 6,
					height: bText.getHeight() + 6,
					stroke: 'black',
					strokeWidth: 1,
					fill: 'white'
				});

				bLabelGroup.add(bRect);
				bLabelGroup.add(bText);

				bLabelGroup.setAttrs({
					x: (center.x - normalize.x) * params.scale + konvaSettings.detailedCanvasOffset + params.beginPoint.x,
					y: (center.y - normalize.y) * params.scale + konvaSettings.detailedCanvasOffset + params.beginPoint.y
				});
				sizeGroups.push(bLabelGroup);

				const lLine = lines[1];
				const maxWidth = Math.max(lLine[0], lLine[2]);

				text = String(UnitConverter.getConvertValue(
					shape.depth, 'distance', 'inches', 3
				));
				center = Utils.center(maxWidth, lLine[1], maxWidth, lLine[3]);

				const lText: Konva.Text = new Konva.Text({
					text: text,
					fontSize: Math.min((params.zoom - 100) / 10 + 10, MAX_SCALE_VALUE),
					offsetY: -4,
					offsetX: -3
				});
				const lRect: Konva.Rect = new Konva.Rect({
					width: lText.getWidth() + 6,
					height: lText.getHeight() + 6,
					stroke: 'black',
					strokeWidth: 1,
					fill: 'white'
				});

				lLabelGroup.add(lRect);
				lLabelGroup.add(lText);

				lLabelGroup.setAttrs({
					x: (center.x - normalize.x) * params.scale + konvaSettings.detailedCanvasOffset + params.beginPoint.x + (shape.constructor.name == 'Trapeze' ? 10 : 0),
					y: (center.y - normalize.y) * params.scale + konvaSettings.detailedCanvasOffset + params.beginPoint.y + lRect.width() / 2,
					rotation: -90
				});

				sizeGroups.push(lLabelGroup);
			});
		});

		return sizeGroups;
	}

	drawHiddenLines(canvasElement: Konva.Group): Promise<Konva.Rect> {
		return new Promise((resolve) => {
			if (this._stripesImage.complete) {
				const stripesRect: Konva.Rect = new Konva.Rect({
					name: 'stripes',
					x: 1,
					y: 1,
					width: Math.max(canvasElement.width() - 2, 0),
					height: Math.max(canvasElement.height() - 2, 0),
					fillPatternImage: this._stripesImage,
					fillPatternRepeat: 'repeat',
					fillPatternScaleX: 20 / this._stripesImage.width,
					fillPatternScaleY: 20 / this._stripesImage.height
				});
				canvasElement.add(stripesRect);
				resolve(stripesRect);
			} else {
				this._stripesImage.onload = () => this.drawHiddenLines(canvasElement);
			}
		});
	}
}

